import type { ReactNode } from 'react';

/**
 * RIM-178 - a linha discreta do cartão de métrica.
 *
 * SVG à mão, sem biblioteca. São dois `<path>` e uma escala linear; puxar uma
 * dependência de gráficos para isso custaria mais peso de bundle do que o
 * desenho inteiro.
 *
 * Ela nunca aparece sozinha: o servidor só manda `series` quando há sete dias
 * ou mais **e** os valores variam (`metrics.service.ts`). Uma reta plana ocupa
 * espaço fingindo informar.
 *
 * **Decorativa por decisão.** O número que importa está grande, logo acima, e
 * a comparação com o período anterior está escrita em texto ao lado. A linha
 * mostra o *formato* do período — subiu no fim, foi tudo num dia só — e quem
 * usa leitor de tela já recebeu a informação em palavras. Por isso
 * `aria-hidden`: anunciar "gráfico" aqui seria repetir o que já foi dito.
 */
export function Sparkline({
  values,
  tone = 'brand',
}: {
  values: number[];
  tone?: 'brand' | 'muted';
}): ReactNode {
  if (values.length < 2) return null;

  const width = 100;
  const height = 24;
  const max = Math.max(...values);
  const min = Math.min(...values);
  const span = max - min || 1;

  const pontos = values.map((valor, indice) => {
    const x = (indice / (values.length - 1)) * width;
    // O eixo Y do SVG cresce para baixo; 1 px de folga em cima e embaixo para
    // a espessura do traço não ser cortada na borda.
    const y = height - 1 - ((valor - min) / span) * (height - 2);
    return `${x.toFixed(2)},${y.toFixed(2)}`;
  });

  const linha = `M ${pontos.join(' L ')}`;
  const area = `${linha} L ${width},${height} L 0,${height} Z`;
  const cor = tone === 'brand' ? 'var(--color-brand)' : 'var(--color-ink-faint)';

  return (
    <svg
      viewBox={`0 0 ${width} ${height}`}
      preserveAspectRatio="none"
      aria-hidden="true"
      className="mt-2 h-6 w-full"
    >
      <path d={area} fill={cor} opacity="0.08" />
      <path
        d={linha}
        fill="none"
        stroke={cor}
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
        vectorEffect="non-scaling-stroke"
      />
    </svg>
  );
}
